import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  selectOption,
} from '@/components/ui/multi-select-utils';
import { userTypeaheadSearch } from '@/lib/api-call-hooks';
import { getStudentsWithGLARecords, updateStudentGLA } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { GlaFollowUpStatus, glaFollowUpStatusSchema, Role, StudentWithGLARecord } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const followUpStatusLabelMap: Record<GlaFollowUpStatus, string> = {
  [GlaFollowUpStatus.need_follow_up]: 'Need follow-up',
  [GlaFollowUpStatus.check_again]: 'Check again',
  [GlaFollowUpStatus.resolved]: 'Resolved',
};

const editStudentGLAFormSchema = z.object({
  glaFollowUpStatus: glaFollowUpStatusSchema,
  reviewer: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  glaReviewNotes: z.string().optional(),
});

type EditStudentGLAForm = z.infer<typeof editStudentGLAFormSchema>;

export interface EditStudentGLAProps {
  student: StudentWithGLARecord;
  trigger: ReactNode;
}

export const EditStudentGLA = ({ student, trigger }: EditStudentGLAProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    mutationFn: updateStudentGLA,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<EditStudentGLAForm>) => {
    try {
      await mutateAsyncUpdate({
        studentId: student.id,
        data: {
          glaFollowUpStatus: formData.glaFollowUpStatus,
          glaReviewerId: formData.reviewer.value,
          glaReviewNotes: formData.glaReviewNotes,
        },
      });

      await queryClient.invalidateQueries({ queryKey: getStudentsWithGLARecords().queryKey });

      toast({ description: 'GLA review updated successfully' });
      closeModal();
    } catch (e: unknown) {
      toast({
        description: airServerErrorFrom(e)?.userFacingMessage ?? 'Failed to update GLA review. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const defaultValues: DefaultValues<EditStudentGLAForm> = {
    glaFollowUpStatus: student.glaFollowUpStatus ?? undefined,
    reviewer: student.glaReviewer ? buildUserSelectOptions(student.glaReviewer) : emptySelectOption,
    glaReviewNotes: student.glaReviewNotes ?? undefined,
  };

  return (
    <FormModal
      modalTitle="GLA follow-up"
      formSchema={editStudentGLAFormSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel="Save"
      shouldResetOnCancel
    >
      {({ form }) => (
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-2">
            <FormComboBox
              form={form}
              label="Reviewer"
              name="reviewer"
              placeholder="Select admin"
              {...comboBoxOptionsOf(userTypeaheadSearch(queryClient, { roles: [Role.air_admin] }))}
              triggerProps={{ className: 'w-full' }}
              triggerSearchOnFocus
              required
            />
            <FormSingleSelect
              name="glaFollowUpStatus"
              label="Follow Up Status"
              form={form}
              options={glaFollowUpStatusSchema.options}
              renderOption={(value: GlaFollowUpStatus) => followUpStatusLabelMap[value]}
              triggerProps={{ className: 'w-full' }}
            />
          </div>
          <FormTextArea name="glaReviewNotes" label="Review Notes" form={form} />
        </div>
      )}
    </FormModal>
  );
};
